import type { CityConfig } from "@/pages/city-template/types";

export const sandyConfig: CityConfig = {
  city: "Sandy",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "sandy",
  route: "/cities/sandy",
  title: "Emergency Passport Sandy OR | Expedited Passport Renewal Mt Hood Gateway",
  metaDescription: "Emergency passport and expedited passport renewal in Sandy, OR — as fast as 24 hours. Fast Passport Center serves Sandy, the Mt. Hood gateway, and eastern Clackamas County travelers. Registered State Dept. courier. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Sandy, Oregon",
  heroSubtitle: "Passport expired before a Canadian hunting trip, Alaska cruise, or last-minute flight from PDX? Fast Passport Center helps Sandy and Mt. Hood corridor residents get their emergency passport or expedited renewal done fast — as fast as 24 hours. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Sandy%20Oregon%20downtown%20gateway%20town%20with%20iconic%20Mount%20Hood%20snowcapped%20volcano%20rising%20dramatically%20above%20forested%20Pacific%20Northwest%20foothills%2C%20charming%20small%20town%20main%20street%20lined%20with%20evergreen%20trees%20and%20welcoming%20storefronts%2C%20soft%20golden%20afternoon%20light%2C%20warm%20amber%20and%20green%20tones%2C%20photorealistic%20Pacific%20Northwest%20landscape%20photography%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=sandy-or-hero-01&orientation=landscape",
  heroImageAlt: "Sandy Oregon downtown with Mount Hood snowcapped volcano visible above Pacific Northwest forested hills",
  geoPosition: "45.3976;-122.2615",
  geoPlacename: "Sandy, Oregon",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 190 miles north — 3.5 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Sandy", zips: "97055" },
    { name: "Estacada", zips: "97023" },
    { name: "Boring", zips: "97009" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Damascus", zips: "97089" },
    { name: "Troutdale", zips: "97060" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Oregon City", zips: "97045" },
    { name: "Clackamas", zips: "97015" },
    { name: "Portland", zips: "97201–97299" },
    { name: "Government Camp", zips: "97028" },
    { name: "Welches", zips: "97067" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required. Complete your expedited passport order from your Sandy home in minutes — straightforward even if it's your first time." },
    { icon: "ri-truck-line", title: "Ship Overnight from Sandy Area FedEx or UPS", desc: "Sandy has UPS and FedEx shipping options — overnight your documents and we handle the agency submission." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed before submission. Critical for outdoor and rural travelers who have fewer in-person resources." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Slots", desc: "We bypass the public appointment system entirely — reserved submission slots at the Seattle Passport Agency are included." },
  ],
  testimonials: [
    {
      name: "Derek L.",
      location: "Sandy, OR",
      rating: 5,
      text: "Elk hunting trip to British Columbia — passport expired three years ago and I found out two weeks before the season opener. Fast Passport Center was amazing. Shipped from the Sandy UPS Store and had my renewed passport in 7 days. Incredible service.",
      service: "Emergency Passport",
    },
    {
      name: "Colleen M.",
      location: "Sandy, OR",
      rating: 5,
      text: "Alaska cruise from Seattle — our two kids had never had passports. Fast Passport Center guided us through the child passport process step by step. Both passports arrived in 9 days. We made the cruise.",
      service: "Child Passport",
    },
    {
      name: "Roger F.",
      location: "Boring, OR",
      rating: 5,
      text: "Business trip to Vancouver BC came up out of nowhere. My passport had expired during COVID. Fast Passport Center got it renewed in 5 business days — I shipped overnight from the Sandy FedEx. Can't say enough good things.",
      service: "Expedited Passport Renewal",
    },
  ],
  facilities: [
    {
      id: "sandy-post",
      name: "Sandy Post Office",
      type: "USPS Passport Acceptance",
      address: "37323 Proctor Blvd",
      city: "Sandy",
      state: "OR",
      zip: "97055",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Downtown Sandy — primary local acceptance location",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility in Sandy. Appointment required. Book in advance — summer is busy given Mt. Hood tourism. Photo services available nearby.",
      icon: "ri-mail-line",
    },
    {
      id: "gresham-post",
      name: "Gresham Post Office",
      type: "USPS Passport Acceptance",
      address: "600 NW Division St",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Gresham — about 18 miles west, larger office",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Larger Gresham post office with better appointment availability and photo services for Sandy-area residents.",
      icon: "ri-mail-line",
    },
    {
      id: "boring-post",
      name: "Boring Post Office",
      type: "USPS Passport Acceptance",
      address: "28230 SE Haley Rd",
      city: "Boring",
      state: "OR",
      zip: "97009",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Boring — about 7 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Small rural post office — very limited appointment availability. Sandy or Gresham preferred for acceptance.",
      icon: "ri-mail-line",
    },
    {
      id: "troutdale-post",
      name: "Troutdale Post Office",
      type: "USPS Passport Acceptance",
      address: "120 E Columbia River Hwy",
      city: "Troutdale",
      state: "OR",
      zip: "97060",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Troutdale — about 22 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Troutdale option for Sandy residents commuting toward the Columbia River Gorge corridor.",
      icon: "ri-mail-line",
    },
    {
      id: "oregon-city-post",
      name: "Oregon City Post Office",
      type: "USPS Passport Acceptance",
      address: "610 7th St",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Oregon City — about 25 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Oregon City option for Sandy residents traveling south toward Clackamas County hub.",
      icon: "ri-mail-line",
    },
    {
      id: "clackamas-county-clerk",
      name: "Clackamas County Clerk — Passport Services",
      type: "County Passport Acceptance",
      address: "1710 Red Soils Ct, Suite 100",
      city: "Oregon City",
      state: "OR",
      zip: "97045",
      phone: "503-655-8551",
      hours: "Mon–Fri 8:30am–4:30pm (by appointment)",
      distance: "Oregon City — about 25 miles southwest",
      website: "https://www.clackamas.us/recording",
      websiteLabel: "Clackamas County Clerk",
      notes: "County clerk alternative for passport acceptance — often quicker appointments than USPS. Call ahead.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Sandy, Oregon?",
      a: "Yes. Through Fast Passport Center, Sandy residents can get an emergency passport in as little as 24 hours for urgent travel, or 2–9 business days with expedited processing. Start online, receive your checklist, ship overnight from the Sandy UPS Store or nearby FedEx, and we submit through reserved Seattle agency channels.",
    },
    {
      q: "How fast can I get an expedited passport renewal from Sandy, OR?",
      a: "Expedited passport renewal from Sandy is available through Fast Passport Center in as little as 3 business days. Most adult renewals do not require an in-person acceptance visit. Ship your documents overnight from the Sandy UPS Store — we handle the agency submission.",
    },
    {
      q: "Do Sandy, Oregon residents need to drive to Portland or Seattle for a passport?",
      a: "No. With Fast Passport Center, Sandy residents start online, ship overnight from local FedEx or UPS, and we submit through reserved Seattle agency slots. No drive to Seattle or Portland required for most services.",
    },
    {
      q: "Where do Sandy residents get their passport photo taken?",
      a: "Walgreens in Sandy offers passport photo services (37617 Hwy 26). The UPS Store in Sandy also offers passport photos. For a wider selection, Gresham has FedEx Office, Walgreens, and Walmart all within 20 minutes.",
    },
    {
      q: "Can I get a child passport for a British Columbia trip from Sandy?",
      a: "Yes, though child passports (for minors under 16) require an in-person acceptance step with both parents typically present. The Sandy Post Office handles this, as does Oregon City or Gresham. Once the acceptance step is done, Fast Passport Center expedites processing — BC hunting and fishing trips often come with short-notice timelines.",
    },
    {
      q: "How long does standard passport processing take from Sandy?",
      a: "Standard USPS processing runs 6–13 weeks everywhere in the country — including Sandy. During peak summer season when Mt. Hood travel is heaviest, it can stretch to 14+ weeks. If your trip is in under 10 weeks, use Fast Passport Center's expedited courier service.",
    },
    {
      q: "What if I lost my passport before a trip from Sandy?",
      a: "Act immediately. Complete Form DS-64 to report the lost or stolen passport, then apply for an emergency replacement. Fast Passport Center guides Sandy-area travelers through this process and can expedite replacement as fast as 24 hours for eligible urgent travel.",
    },
    {
      q: "What airport do Sandy residents use for international travel?",
      a: "Portland International Airport (PDX) is the primary international airport for Sandy residents, about 27 miles west. PDX offers direct service to Amsterdam, London, Reykjavik, Tokyo, Vancouver BC, and Mexico City. Confirm your passport is valid before booking any international flight.",
    },
    {
      q: "Does Sandy, Oregon have a passport agency?",
      a: "Sandy does not have a regional passport agency. The nearest is the Seattle Passport Agency, about 190 miles north. For most Sandy residents, Fast Passport Center's remote expedited service is faster and more convenient than the 7-hour round trip to Seattle.",
    },
    {
      q: "Can I get my passport expedited without going to Portland?",
      a: "Yes. Fast Passport Center allows Sandy-area residents to start online, ship from local FedEx or UPS, and receive their completed passport by mail — no Portland office visit required. We submit through reserved agency channels and handle everything remotely.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Sandy", address: "37617 Hwy 26, Sandy, OR 97055", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Sandy area", address: "17045 SE Stark St, Sandy, OR 97055", hours: "Mon–Fri 8 AM – 7 PM, Sat 9 AM – 5 PM" },
    { name: "Walmart — Gresham", address: "2255 NE Burnside Rd, Gresham, OR 97030", hours: "Daily 7 AM – 11 PM" },
    { name: "Walgreens — Gresham", address: "1501 NW Burnside Rd, Gresham, OR 97030", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Gresham", address: "1501 NE Halsey St, Gresham, OR 97230", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "CVS — Troutdale / Fairview area", address: "23715 NE Sandy Blvd, Troutdale, OR 97060", hours: "Daily 7 AM – 10 PM" },
    { name: "Walgreens — Oregon City", address: "19000 S Beavercreek Rd, Oregon City, OR 97045", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Clackamas", address: "16301 SE 82nd Dr, Clackamas, OR 97015", hours: "Daily 7 AM – 11 PM" },
    { name: "Target — Happy Valley", address: "9190 SE 82nd Ave, Happy Valley, OR 97086", hours: "Daily 8 AM – 10 PM" },
    { name: "FedEx Office — Portland SE", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM" },
  ],
  dmvLocations: [
    {
      name: "Gresham Driver & Motor Vehicle Services",
      address: "285 W Powell Blvd, Gresham, OR 97030",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Nearest full DMV for Sandy residents. Driver licenses, REAL ID, ID cards. Sandy has no DMV of its own.",
      featured: true,
    },
    {
      name: "Oregon City Driver & Motor Vehicle Services",
      address: "1021 S Molalla Ave, Oregon City, OR 97045",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Oregon City DMV — southwest option for Sandy residents traveling toward Clackamas County hub.",
      featured: false,
    },
    {
      name: "Clackamas DMV Office",
      address: "5060 SE Hwy 212, Clackamas, OR 97015",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Clackamas — second Clackamas County DMV option.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — about 27 miles west",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX is the main international departure point for Sandy residents, with direct flights to Amsterdam, London, Tokyo, Vancouver BC, and connections to destinations worldwide. A valid U.S. passport is required for all international PDX departures. Peak season runs May–August.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 190 miles north; also Alaska cruise departure point",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Sandy residents occasionally use SEA for Alaska cruises or additional international flight options. Passport required for all international flights and cruises.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Sandy & Mt. Hood Corridor",
      description: "BC hunting trip. Alaska cruise. Last-minute flight from PDX. Whatever the urgency, Fast Passport Center handles emergency passport processing for Sandy-area residents — start online, ship overnight from local FedEx or UPS, we do the rest.",
      bullets: ["As fast as 24 hours", "Reserved Seattle agency submission", "Dedicated agent and live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired during COVID and you never renewed? Sandy-area travelers renew fast through Fast Passport Center — ship overnight, we handle the expedited submission.",
      bullets: ["Fastest renewal option for Sandy residents", "Ship from Sandy UPS or Gresham FedEx overnight", "Document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Sandy residents through DS-64 and expedited replacement — including emergency timelines for urgent BC or international travel.",
      bullets: ["DS-64 reporting guidance", "Expedited replacement timelines", "Pre-submission document review"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Mt. Hood Corridor Families",
      description: "Sandy and Mt. Hood families frequently need child passports for BC camping and Alaska cruises. We provide the acceptance checklist — you do the Sandy Post Office step, we expedite from there.",
      bullets: ["Both parents typically required", "Sandy Post Office guidance included", "Expedited after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First-time passport from Sandy — we give you a complete checklist for the acceptance clerk visit at the Sandy or Gresham post office and handle expedited processing after.",
      bullets: ["Step-by-step acceptance checklist", "Photo location guide", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels with a best price guarantee, typically 30–100% less than FedEx Office, Staples, and resellers.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Passport Services",
      description: "Name change after marriage or divorce, damaged passport replacement, second passports for frequent international travel — all available with our expedited courier access.",
      bullets: ["Name change passports", "Damaged passport replacement", "Second passport for business travelers"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Sandy, Oregon travelers with urgent departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Sandy, OR and Mt. Hood corridor residents needing passports faster than standard government timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Sandy, Oregon residents — ship overnight from local FedEx or UPS, we handle the rest." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Sandy travelers with lost or stolen passports before travel." },
    { name: "Child Passport", description: "Expedited child passport services for Sandy, Oregon families heading to British Columbia, Alaska cruises, or international destinations." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission serving Sandy, Oregon and the Mt. Hood gateway corridor." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Until the Hunting or Camping Season Starts", description: "Sandy-area outdoor enthusiasts routinely book last-minute BC trips and discover their passport expired mid-summer. Standard processing takes 6–13 weeks. If British Columbia hunting or fishing season is approaching, renew now — not when the tags arrive." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstore photo booths often get rejected for lighting, background, or expression. We review photo specs before submission to prevent this." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrectly Signed Forms", description: "One missing signature or wrong date gets your application returned — losing weeks. Our pre-submission review catches these errors before they become problems." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Not all birth certificates are accepted. Some require a certified long-form copy with a raised seal. Fast Passport Center specifies exactly what you need." },
    { icon: "ri-calendar-close-line", title: "Assuming Summer Is a Good Time to Apply Through USPS", description: "Peak travel season is the worst time for standard USPS processing — wait times stretch to 14+ weeks in summer. Use Fast Passport Center's expedited service if your trip is in under 10 weeks." },
    { icon: "ri-map-pin-time-line", title: "Six-Month Validity Rule for Canada and Other Countries", description: "Many travelers don't realize Canada requires passports to be valid for the duration of your stay, while most other countries require six months beyond your return date. Check before booking." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Sandy-area travel date. Complete your secure order online in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send your personalized document checklist — exactly what to gather, with photo location recommendations near Sandy." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Sandy or Gresham", description: "Drop off at the Sandy UPS Store or Gresham FedEx — most locations are open evenings for Mt. Hood corridor residents." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through our reserved Seattle Passport Agency slots — no waiting for public appointment cancellations." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped Directly to Sandy Area", description: "Your completed passport ships directly to your Sandy-area address. Track status in real time." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Seattle — 3.5 hrs each way from Sandy",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Not practical for most Sandy residents",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable processing",
        "Ship from Sandy UPS or Gresham FedEx",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Document review included",
        "Dedicated agent",
        "Daily status updates",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail — no courier access",
        "6–13 weeks average; 14+ weeks in summer",
        "In-person clerk appointment required",
        "No document review",
        "No dedicated agent",
        "Cannot upgrade after submission",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3.5 hours each way)", fpc: "Ship overnight from Sandy area FedEx or UPS" },
    { feature: "Processing Speed", agency: "Strict eligibility — urgent only", fpc: "As fast as 24 hours — multiple speed tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural & Gateway Access", agency: "Not designed for rural Mt. Hood corridor residents", fpc: "Fully remote — no Portland or Seattle office trip" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees only — but 7-hr Seattle round trip costs you", fpc: "Guaranteed best price — typically 30–100% less than resellers" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Emergency passport services near Estacada", route: "/cities/estacada" },
    { name: "Expedited passport help in Gresham", route: "/cities/gresham" },
    { name: "Fast passport renewal in Troutdale", route: "/cities/troutdale" },
    { name: "Rush passport services in Oregon City", route: "/cities/oregon-city" },
    { name: "Expedited passports for Oregon travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from the Sandy UPS Store or Gresham FedEx — most are open evenings. No visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Sandy and the Mt. Hood gateway corridor remotely",
  serviceAreaIntro: "Whether you are in Sandy, Boring, Government Camp, Welches, or anywhere along the Mt. Hood corridor, Fast Passport Center makes emergency and expedited passport processing accessible — start online, ship overnight, and we handle the agency submission.",
  nearbyCitiesIntro: "We serve all Sandy, Boring, and Mt. Hood corridor zip codes. Ship overnight from the Sandy UPS Store and your documents arrive at our processing center the next morning.",
  howItWorksIntro: "Getting an emergency or expedited passport from the Mt. Hood gateway is simpler than you think. Most Sandy clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays and rejections for Sandy and Mt. Hood corridor passport applicants. Fast Passport Center reviews your documents before submission to prevent them.",
  testimonialsIntro: "Real reviews from Sandy, Boring, and Mt. Hood corridor travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Sandy, Oregon and Mt. Hood gateway travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  travelTiming: {
    title: "Travel & Passport Timing for Sandy and Mt. Hood Corridor Residents",
    intro: "Sandy is Oregon's official Mt. Hood gateway — and its residents travel with the rhythms of mountain recreation, outdoor adventure, and Pacific Northwest seasons. From British Columbia hunting seasons to Alaska cruise departures and international flights from PDX, here's when passport urgencies arise most in the Sandy area.",
    cards: [
      {
        icon: "ri-landscape-line",
        title: "British Columbia Hunting, Fishing & Outdoor Recreation",
        subtitle: "Canada border crossings for outdoor adventures — Sandy area's top driver of passport urgency",
        description: "Sandy-area hunters, anglers, and outdoor enthusiasts cross into British Columbia regularly — elk and moose hunting in the BC Interior, steelhead fishing on Vancouver Island, and backcountry adventures in the Cariboo. These trips are often planned when game units open and available — sometimes with only 2–3 weeks notice. A valid U.S. passport is required for any Canadian air travel, and strongly recommended for land crossings. Many Sandy-area hunters discover their passport expired during the COVID years and never renewed.",
        tip: "BC big game hunting requires a valid U.S. passport for any air travel between Oregon and British Columbia. If you're hunting the BC Interior via flight, you need a passport that is valid for the duration of your trip. Don't discover this when you're packing.",
      },
      {
        icon: "ri-ship-line",
        title: "Alaska Cruise Departures from Seattle",
        subtitle: "Mt. Hood corridor families booking Alaska cruises — peak May through September",
        description: "Alaska cruises from Seattle are enormously popular with Sandy, Boring, and Mt. Hood corridor families — a summer splurge that combines spectacular scenery with manageable drive distance to the Seattle port. Last-minute cruise deals (booked 2–4 weeks before sailing) frequently catch families off guard with expired passports or children who have never had one. Both situations are solvable with Fast Passport Center's expedited service — but not with standard post office processing.",
        tip: "Alaska cruises require a passport for all passengers even for closed-loop sailings from Seattle. Enhanced driver licenses (only available to Washington residents) are not an option for Oregon passengers. Get a passport for every family member before booking.",
      },
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX International Departures",
        subtitle: "Portland International Airport — about 27 miles west via Hwy 26",
        description: "PDX is the primary international departure point for Sandy residents and covers a wide range of destinations: Amsterdam, London, Reykjavik, Frankfurt, Tokyo, Vancouver BC, and Mexico City via direct service. Summer is peak booking season, and many Sandy-area families realize their passports are expired or within the 6-month validity window after booking rather than before. Don't let a $200 expedited fee prevent a $5,000 family vacation.",
        tip: "Book PDX international flights only after confirming your passport expiration date AND the six-month validity requirement for your destination country. Mexico, Europe, and Japan all enforce this rule strictly.",
      },
      {
        icon: "ri-global-line",
        title: "Teaching Abroad & Mission Travel",
        subtitle: "Young Sandy-area residents in teaching abroad and mission programs",
        description: "Sandy and the Mt. Hood corridor have a significant population of young adults in teaching English abroad programs and international mission organizations. These programs typically require passports to be submitted months before departure for visa processing — meaning a passport that expires in less than a year at application time can become a problem. Fast Passport Center handles rush renewals for program applicants whose passports are expiring before their program departure date.",
        tip: "Teaching English abroad programs in Japan, Korea, and China often require 12+ months of passport validity remaining at the time of application — not at departure. If your passport expires in under 14 months, renew before applying to any teaching abroad program.",
      },
    ],
    callout: {
      icon: "ri-snowflake-line",
      title: "Government Camp & Welches — High-Mountain Resort International Travel",
      description: "Mt. Hood's resort communities — Government Camp and Welches — attract seasonal workers, ski instructors, and outdoor guide operators who travel internationally for certifications, instructor exchanges, and travel partnerships. These professionals often carry complex passport situations (name changes, multiple passports, Canadian work permits) and need fast resolution. Fast Passport Center handles complex expedited passport cases for Mt. Hood corridor outdoor industry professionals.",
    },
  },
  destinations: {
    title: "Where Sandy & Mt. Hood Corridor Travelers Go — And Why Passports Get Urgent",
    intro: "Sandy is surrounded by Oregon's most spectacular outdoor recreation and is a transit point for Pacific Northwest international travelers. These are the destinations and travel patterns that drive passport urgency in this corridor.",
    stats: [
      { stat: "27 mi", label: "to PDX — Portland International Airport from Sandy" },
      { stat: "2–3 Wks", label: "typical notice when Sandy-area hunters and anglers book BC trips" },
      { stat: "Jun–Sep", label: "peak Alaska cruise and Canadian border crossing season from Sandy area" },
      { stat: "24 Hrs", label: "fastest emergency passport processing available to Sandy, OR residents" },
    ],
    destinations: [
      { name: "British Columbia, Canada", context: "Hunting in the BC Interior, steelhead fishing on Vancouver Island, Whistler skiing, backcountry adventures — Sandy's #1 international destination", urgency: "BC hunting seasons open on unpredictable schedules — tags arrive with 2-3 weeks notice", icon: "ri-landscape-line" },
      { name: "Alaska — Cruise from Seattle", context: "Summer Alaska cruises from Seattle are a top vacation choice for Mt. Hood corridor families", urgency: "Last-minute cruise deals booked 2–4 weeks before sailing routinely catch families with expired passports", icon: "ri-ship-line" },
      { name: "Mexico", context: "Winter beach vacations to Cancún, Cabo, and Puerto Vallarta — popular after Mt. Hood ski season closes", urgency: "Off-season winter getaways are often booked 10–21 days in advance", icon: "ri-map-pin-2-line" },
      { name: "Europe", context: "Summer family trips to UK, France, Italy; heritage travel; professional conferences from Portland tech corridor", urgency: "Peak European travel season coincides with peak Oregon summer — passports often discovered expired in April for June trips", icon: "ri-map-pin-2-line" },
      { name: "Japan & South Korea", context: "Teaching English abroad programs (very popular with Mt. Hood area young adults); Japan ski exchange programs; anime tourism", urgency: "Teaching abroad applications require passport validity months before departure — programs often reject applicants whose passports expire within a year", icon: "ri-map-pin-2-line" },
      { name: "Costa Rica & Central America", context: "Eco-adventure travel popular with Sandy-area outdoor enthusiasts — zip-lining, surfing, wildlife tours, Pacific coast beaches", urgency: "Adventure travel tends to be booked 3–6 weeks out by active Oregon residents", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Nearby Oregon Service Areas",
    crossLinksDesc: "We serve travelers throughout the Mt. Hood corridor and Portland metro with the same expedited passport expertise.",
    crossLinks: [
      { name: "Estacada", route: "/cities/estacada" },
      { name: "Troutdale", route: "/cities/troutdale" },
      { name: "Gresham", route: "/cities/gresham" },
    ],
  },
};